import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Calendar, MapPin, Users, Clock, ExternalLink } from 'lucide-react';
import { Activity, ActivityWithApplication } from '../types/activity';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api.service';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { ApplicationStatus } from '../types/application';
import { toast } from 'sonner';
import { usePermissions } from '../hooks/userPermissions';
import { ApplicationModal } from './ApplicationModal';


interface FlipCardProps {
    activity: ActivityWithApplication;
    onCreatorClick: (creatorId: string) => void;
    onJoinActivity: (activityId: string) => void;
}

export function FlipCard({ activity, onCreatorClick, onJoinActivity }: FlipCardProps) {
    const [isFlipped, setIsFlipped] = useState(false);
    const [numberOfParticipations, setNumberOfParticipations] = useState(0);
    const [showApplicationModal, setShowApplicationModal] = useState(false);
    const { user, isUser, isCreator } = useAuth();
    const { canJoinActivities } = usePermissions();
    const navigate = useNavigate();

    const date = new Date(activity.dateTime).toLocaleDateString();
    const time = new Date(activity.dateTime).toLocaleTimeString();

    const getNumberOfParticipations = async (activity: Activity) => {
        const participations = await apiService.getParticipationsByActivityId(activity.id);
        setNumberOfParticipations(participations.length)
    }

    const handleViewDetails = (event: React.MouseEvent) => {
        event.stopPropagation();
        navigate(`/activity/${activity.id}`)
    }

    const handleCreatorClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        onCreatorClick(activity.creatorId ? activity.creatorId : '');
    }

    // Call getNumberOfParticipations when component mounts
    useEffect(() => {
        getNumberOfParticipations(activity);
    }, [activity.id]);

    const handleApplyClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        setShowApplicationModal(true);
    }

    const handleSubmitApplication = async (activityId: string, note: string) => {
        if (user) {
            try {
                // before application system, join activity directly
                // await apiService.joinActivity(activity.id, user.id);

                // after application system, create pending application first
                // TODO: need to create the apply modal(do we need to?)
                const existingApplication = await apiService.getApplicationByUserIdActivityId(user.id, activityId);
                if (existingApplication && existingApplication.status === ApplicationStatus.WITHDRAWN) {
                    await apiService.updateApplication(existingApplication.id, ApplicationStatus.PENDING);
                } else {
                    await apiService.createApplication({
                        activityId: activityId,
                        userId: user.id,
                        status: ApplicationStatus.PENDING,
                        note: note
                    });
                }
                // Refresh participation count after joining
                await getNumberOfParticipations(activity);
                toast.success('Application submitted successfully');
                // setIsFlipped(true);
            } catch (error) {
                // console.error('Error joining activity:', error);
                toast.error('You have already applied to this activity');
            }
        }
        onJoinActivity(activity.id);
    }

    // Format activity type for display (SPORTS_FITNESS -> Sports Fitness)
    const formatActivityType = (type: string) => {
        return type
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const containerStyle: React.CSSProperties = {
        height: '320px',
        cursor: 'pointer',
        perspective: '1000px'
    };

    const cardStyle: React.CSSProperties = {
        position: 'relative',
        width: '100%',
        height: '100%',
        transition: 'transform 0.8s',
        transformStyle: 'preserve-3d',
        transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
    };

    const frontStyle: React.CSSProperties = {
        position: 'absolute',
        inset: '0',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden'
    };

    const backStyle: React.CSSProperties = {
        position: 'absolute',
        inset: '0',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        transform: 'rotateY(180deg)'
    };

    return (
        <>
            <div style={containerStyle} onClick={() => setIsFlipped(!isFlipped)}>
                <div style={cardStyle}>
                    {/* Front Side */}
                    <Card style={frontStyle} className="card-hover border-0 shadow-lg dark:shadow-2xl overflow-hidden">
                        <div className="relative h-full overflow-hidden rounded-lg group">
                            <img
                                src={activity.image}
                                alt={activity.title}
                                className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                                <Badge className="mb-2 bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white/30">
                                    {formatActivityType(activity.type)}
                                </Badge>
                                <h3 className="text-lg font-semibold mb-1 drop-shadow-md">{activity.title}</h3>
                                <div className="flex items-center space-x-4 text-sm opacity-90">
                                    <div className="flex items-center space-x-1">
                                        <Calendar size={14} />
                                        <span>{date}</span>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                        <MapPin size={14} />
                                        <span className="truncate max-w-[150px]">{activity.location?.name.split(',')[0]}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Back Side */}
                    <Card style={backStyle} className="border-0 shadow-lg dark:shadow-2xl">
                        <div className="p-6 h-full flex flex-col">
                            <div className="flex justify-between items-start mb-4">
                                <h3 title={activity.title} className="text-lg font-semibold line-clamp-1 break-words">
                                    {activity.title}
                                </h3>
                                <Badge variant="outline">{activity.difficulty}</Badge>
                            </div>

                            <div className="space-y-3 flex-1">
                                <div className="flex items-center space-x-2 text-sm">
                                    <Calendar size={16} className="text-muted-foreground" />
                                    <span>{date} at {time}</span>
                                </div>

                                <div className="flex items-center space-x-2 text-sm">
                                    <MapPin size={16} className="text-muted-foreground" />
                                    <span>{activity.location?.name.split(',')[0]}</span>
                                </div>

                                <div className="flex items-center space-x-2 text-sm">
                                    <Users size={16} className="text-muted-foreground" />
                                    <span>{numberOfParticipations}/{activity.maxParticipants} participants</span>
                                </div>

                                <div className="flex items-center space-x-2 text-sm">
                                    <Clock size={16} className="text-muted-foreground" />
                                    <span>Organized by </span>
                                    <button
                                        onClick={handleCreatorClick}
                                        className="text-primary hover:underline font-medium focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded-sm"
                                    >
                                        {activity.creator?.name || activity.creator?.name}
                                    </button>
                                </div>

                                <p title={activity.description}
                                    className="text-sm text-muted-foreground mt-3 line-clamp-2 break-words"
                                >
                                    {activity.description}
                                </p>
                            </div>

                            <div className="mt-2 pt-2 border-t border-border">
                                <div className="flex justify-between items-center">
                                    <span className="text-lg font-semibold">
                                        ${activity.price}
                                    </span>
                                    <button className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                                        onClick={handleApplyClick}
                                        disabled={!canJoinActivities}
                                    >
                                        {activity.application?.status === ApplicationStatus.PENDING ? 'Applied' : 'Apply to Join'}
                                    </button>
                                </div>
                                <div className="mt-2 pt-2 border-t border-border">
                                    <Button
                                        onClick={handleViewDetails}
                                        variant="outline"
                                        size="sm"
                                        className="w-full flex items-center justify-center space-x-2"
                                    >
                                        <ExternalLink size={14} />
                                        <span>View Details</span>
                                    </Button>
                                </div>
                            </div>

                        </div>
                    </Card>
                </div>
            </div>

            <ApplicationModal
                isOpen={showApplicationModal}
                onClose={() => setShowApplicationModal(false)}
                activity={activity}
                onSubmit={handleSubmitApplication}
            />
        </>
    );
}